import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Download, Loader2, ExternalLink, ShieldAlert,
} from 'lucide-react';
import { useSection } from '../hooks/usePortalData';

export default function PdfViewerPage() {
  const { code } = useParams();
  const { section, loading, error } = useSection('covid');

  const doc = section?.entries?.find(
    e => (e.code || '').toUpperCase() === (code || '').toUpperCase()
  );

  // Full-page redirect to the embeddable target. Both the OCR HTML and the
  // raw PDF send X-Frame-Options which blocks iframing, so a top-level
  // navigation is the only reliable path for direct-link users.
  useEffect(() => {
    if (!doc) return;
    const target = doc.ocr_link || doc.pdf_url;
    if (target) {
      window.location.replace(target);
    }
  }, [doc]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-field">
        <div className="mono text-sm text-3 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-rose-300" /> Chargement du document…
        </div>
      </div>
    );
  }

  if (error || !doc || !(doc.ocr_link || doc.pdf_url)) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-field">
        <div className="max-w-md text-center">
          <div className="mono text-sm text-rose-300 mb-3">
            Document introuvable ou non disponible.
          </div>
          <Link
            to="/covid"
            className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Retour à Dossiers COVID
          </Link>
        </div>
      </div>
    );
  }

  const target = doc.ocr_link || doc.pdf_url;

  // In-flight redirect screen with a manual escape hatch.
  return (
    <div className="min-h-[70vh] bg-field flex items-center justify-center" data-testid={`pdf-viewer-${doc.code}`}>
      <div className="max-w-xl px-6 text-center">
        <div className="eo-classified-strip mb-6" />
        <div className="mono text-[10px] tracking-[0.28em] text-amber-300 uppercase mb-2">
          Dossier {doc.code} · Ouverture du document
        </div>
        <h1 className="serif text-xl md:text-2xl text-1 mb-3">{doc.title}</h1>
        {doc.source_authority && (
          <div className="mono text-[10px] text-3 mb-4 inline-flex items-center gap-1 justify-center">
            <ShieldAlert className="h-3 w-3 text-amber-300/80" /> {doc.source_authority}
          </div>
        )}
        <div className="text-2 text-sm mb-6 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-rose-300" />
          Redirection vers le document…
        </div>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <a
            href={target}
            target="_blank"
            rel="noreferrer"
            data-testid={`pdf-open-tab-${doc.code}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium"
            style={{
              background: 'linear-gradient(180deg, rgba(220,38,38,0.28), rgba(220,38,38,0.14))',
              border: '1px solid rgba(220,38,38,0.6)',
              color: '#fee2e2',
              letterSpacing: '0.06em',
            }}
          >
            <ExternalLink className="h-4 w-4" /> Ouvrir le document
          </a>
          {doc.pdf_url && (
            <a
              href={doc.pdf_url}
              download
              target="_blank"
              rel="noreferrer"
              data-testid={`pdf-download-${doc.code}`}
              className="btn-ghost inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm"
            >
              <Download className="h-4 w-4" /> Télécharger PDF
            </a>
          )}
          <Link
            to="/covid"
            data-testid="pdf-viewer-back"
            className="btn-ghost inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Retour
          </Link>
        </div>
      </div>
    </div>
  );
}
